import Vue from 'vue';
import VueResource from 'vue-resource';
import { Auth } from 'aws-amplify';

Vue.use(VueResource);

// if the config file has a typo, you'll get weird errors here :(
const ENDPOINT = `${process.env.API_ENDPOINT}api/v1/`;

/** --------------------
 *
 *    Helper functions
 *
 * --------------------- */

const getRequest = (url) =>
  Auth.currentSession().then(session => session.getIdToken().getJwtToken()).then(token =>
    Vue.http
      .get(`${ENDPOINT}${url}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => res.json()));

const postRequest = (url, body) =>
  Auth.currentSession().then(session => session.getIdToken().getJwtToken()).then(token =>
    Vue.http.post(`${ENDPOINT}${url}`, body, {
      headers: { Authorization: `Bearer ${token}` }
    }));

const putRequest = (url, body) =>
  Auth.currentSession().then(session => session.getIdToken().getJwtToken()).then(token =>
    Vue.http.put(`${ENDPOINT}${url}`, body, {
      headers: { Authorization: `Bearer ${token}` }
    }));

const deleteRequest = (url) =>
  Auth.currentSession().then(session => session.getIdToken().getJwtToken()).then(token =>
    Vue.http.delete(`${ENDPOINT}${url}`, {
      headers: { Authorization: `Bearer ${token}` }
    }));

const postAndGetFile = (url, body) =>
  Auth.currentSession().then(session => session.getIdToken().getJwtToken()).then(token =>
    Vue.http.post(`${ENDPOINT}${url}`, body, {
      responseType: 'arraybuffer',
      headers: { Authorization: `Bearer ${token}`, accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }
    }));

// TODO - add pagination to (most/some) of these

/** --------------------
 *
 *    Customer endpoints
 *
 * --------------------- */

const getCustomers = () => getRequest('customers');

const addCustomer = (newCustomer) => postRequest('customers', newCustomer);

/** --------------------
 *
 *    Programme endpoints
 *
 * --------------------- */

const getProgrammes = () => getRequest('programmes');

const addProgramme = (programmeName) => {
  // eslint-disable-line camelcase
  const body = {
    name: programmeName
  };

  return postRequest('programmes', body);
};

/** --------------------
 *
 *    Survey endpoints
 *
 * --------------------- */

/**
 * @TODO - MOVE reporting and analysis to REporting.js
 */

const getSurvey = (id) => getRequest(`surveys/${id}`);

const getSurveys = () => getRequest('surveys');

const getSurveyRuns = () => getRequest('survey-runs');

const searchSurveyRuns = (searchBody) => postRequest('survey-runs/search', searchBody);

const authenticated = (surveyMonkeyToken) => postRequest('authenticated', { surveyMonkeyToken });

const getSurveyResponsePage = (id, page) => getRequest(`surveys/${id}/responses/page/${page}`);

const getExcelReport = (surveyRunId, dimensionData, type) => postAndGetFile(`survey-runs/${surveyRunId}/xlsx?type=${type}`, dimensionData);

const getMultiReport = (surveyRunIds) => postAndGetFile('survey-runs/multi/xlsx', surveyRunIds);

const getScores = (surveyRunIds, dimensionData) => postRequest(`survey-runs/${surveyRunIds.length > 1 ? 'multiple' : surveyRunIds[0]}/analyze?${surveyRunIds.length > 1 ? `survey_run_id=${JSON.stringify(surveyRunIds)}` : ''}`, dimensionData);

const getIndustryAverages = (programmeId) => getRequest(`programmes/${programmeId}/industry-averages`);

const getSurveyRunSummary = (surveyRunId) => getRequest(`survey-runs/${surveyRunId}/summary`);

const deleteSurveyRun = (surveyRunId) => deleteRequest(`survey-runs/${surveyRunId}`);

const getRecommendations = (surveyRunId) => getRequest(`survey-runs/${surveyRunId}/recommendations`);

const saveRecommendation = (recommendationId, surveyRunId, recommendation) => recommendationId ? postRequest(`survey-runs/${surveyRunId}/recommendations/${recommendationId}`, { recommendation }) : postRequest(`survey-runs/${surveyRunId}/recommendations`, recommendation);

const getQuickWins = (surveyRunId) => getRequest(`survey-runs/${surveyRunId}/quick-wins`);

const saveQuickWin = (quickWinId, surveyRunId, quickWin) => quickWinId ? postRequest(`survey-runs/${surveyRunId}/quick-wins/${quickWinId}`, { quick_win: quickWin }) : postRequest(`survey-runs/${surveyRunId}/quick-wins`, quickWin);

const deleteQuickWin = (quickWinId, surveyRunId) => deleteRequest(`survey-runs/${surveyRunId}/quick-wins/${quickWinId}`);

const deleteRecommendation = (recommendationId, surveyRunId) => deleteRequest(`survey-runs/${surveyRunId}/recommendations/${recommendationId}`);

const deleteGoalSetting = (surveyRunId, goalSettingId) => deleteRequest(`survey-runs/${surveyRunId}/goal-setting/${goalSettingId}`);

const deleteGPCSession = (customerId, sessionId) => deleteRequest(`customers/${customerId}/gpc-sessions/${sessionId}`);

const saveGPCSession = (customerId, gpcSessionId, session) => gpcSessionId ? postRequest(`customers/${customerId}/gpc-sessions/${gpcSessionId}`, session) : postRequest(`customers/${customerId}/gpc-sessions`, session);

const searchGPCSessions = (searchBody) => postRequest(`customers/${searchBody.customer_id ? searchBody.customer_id : 'all'}/gpc-sessions/search`, searchBody);

const searchQuickWinsAndRecommendations = (type, searchBody) => postRequest(`survey-runs/all/${type}/search`, searchBody);

const searchGoalSettings = (searchBody) => postRequest(`survey-runs/all/goal-setting/search`, searchBody);

const saveGoalSetting = (surveyRunId, goalSettingId, body) => goalSettingId ? putRequest(`survey-runs/${surveyRunId}/goal-setting/${goalSettingId}`, body) : postRequest(`survey-runs/${surveyRunId}/goal-setting`, body);

const addSurveyRun = (body) => postRequest(`survey-runs`, body);

const updateSurveyRun = (surveyRunId, body) => putRequest(`survey-runs/update/${surveyRunId}`, body);

const addResponsesToSurveyRun = async (surveyRunId, surveyMonkeyId, questions) => {

  const surveyRunResponse = await putRequest(`survey-runs/${surveyRunId}`, { questions });

  const firstPage = await getSurveyResponsePage(surveyMonkeyId, 1);
  await postRequest(`survey-runs/${surveyRunId}/responses`, firstPage);
  if (firstPage.total > firstPage.per_page) {
    for (let i = 2; i <= Math.ceil(firstPage.total / firstPage.per_page); i++) {
      const page = await getSurveyResponsePage(surveyMonkeyId, i); // eslint-disable-line no-await-in-loop
      await postRequest(`survey-runs/${surveyRunId}/responses`, page); // eslint-disable-line no-await-in-loop
    }
  }
};

export {
  getCustomers,
  addCustomer,
  getProgrammes,
  addProgramme,
  getSurvey,
  getSurveys,
  getSurveyRuns,
  searchSurveyRuns,
  getExcelReport,
  addSurveyRun,
  getSurveyRunSummary,
  getScores,
  getMultiReport,
  saveRecommendation,
  getRecommendations,
  saveQuickWin,
  getQuickWins,
  searchQuickWinsAndRecommendations,
  saveGPCSession,
  searchGPCSessions,
  deleteSurveyRun,
  getIndustryAverages,
  deleteQuickWin,
  deleteRecommendation,
  deleteGPCSession,
  addResponsesToSurveyRun,
  searchGoalSettings,
  deleteGoalSetting,
  saveGoalSetting,
  updateSurveyRun,
  authenticated
};




// WEBPACK FOOTER //
// ./src/services/API.js